import { useState, useEffect } from "react";
import {
  VStack,
  Input,
  Select,
  Box,
  Text,
  Image,
  HStack,
  Tag,
  useColorModeValue,
  IconButton,
  Tooltip,
  Button,
} from "@chakra-ui/react";
import {
  TModelSearch,
  EModelArchitecture,
  TModelSearchResult,
  User,
} from "types";
import { aiAPI } from "services/ai-api";
import debounce from "lodash/debounce";
import { ChevronDownIcon, ExternalLinkIcon, LinkIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { userAPI } from "services";
interface ModelSearchProps {
  onModelSelect: (model: TModelSearchResult) => void;
}

export const ModelSearch = ({ onModelSelect }: ModelSearchProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState<string>("checkpoint");
  const [architecture, setArchitecture] = useState<string>("sdxl");
  const [results, setResults] = useState<TModelSearchResult[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const LIMIT = 10;
  const [user, setUser] = useState<User | null>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.100", "gray.700");

  useEffect(() => {
    const fetchUser = async () => {
      const user = await userAPI.getCurrentUser();
      setUser(user);
    };
    fetchUser();
  }, []);

  const checkUserAge = () => {
    const birthDate = new Date(user?.birthday || "");
    console.log(birthDate);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    return age >= 18;
  };

  const debouncedSearch = debounce(
    async (searchParams: TModelSearch, isLoadMore: boolean = false) => {
      try {
        const response = await aiAPI.searchModelsRunware({
          ...searchParams,
          limit: LIMIT,
          offset: isLoadMore ? offset : 0,
        });

        setResults((prev) =>
          isLoadMore ? [...prev, ...response.results] : response.results
        );
        setHasMore(response.results.length === LIMIT);
        if (!isLoadMore) {
          setOffset(0);
        }
      } catch (error) {
        console.error("Error searching models:", error);
      }
    },
    300
  );

  const nsfwTags = [
    "nsfw",
    "pussy",
    "porn",
    "nude",
    "nudes",
    "naked",
    "sex",
    "erotic",
    "hentai",
    "adult",
    "ass",
    "tits",
    "breasts",
    "blowjob",
    "cum",
    "cumshot",
    "cumming",
    "fuck",
    "fucked",
    "fucking",
    "orgasm",
    "mature",
    "milf",
  ];

  const isNSFW = (tags: string[]) => {
    if (checkUserAge()) return false;
    return tags.some((tag) => nsfwTags.includes(tag));
  };

  const handleLoadMore = () => {
    const newOffset = offset + LIMIT;
    setOffset(newOffset);

    const searchParams: TModelSearch = {};
    if (searchTerm) searchParams.search = searchTerm;
    if (category) searchParams.category = category as any;
    if (architecture)
      searchParams.architecture = architecture as EModelArchitecture;

    debouncedSearch(searchParams, true);
  };

  useEffect(() => {
    // Reset results and pagination when search parameters change
    setResults([]);
    setOffset(0);
    setHasMore(false);

    const searchParams: TModelSearch = {};
    if (searchTerm) searchParams.search = searchTerm;
    if (category) searchParams.category = category as any;
    if (architecture)
      searchParams.architecture = architecture as EModelArchitecture;

    debouncedSearch(searchParams);

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, category, architecture]);

  return (
    <VStack spacing={4} width="100%">
      <HStack width="100%" spacing={4}>
        <Select
          // placeholder="All Categories"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="checkpoint">Checkpoint</option>
          <option value="lora" disabled>
            LoRA
          </option>
          <option value="controlnet" disabled>
            ControlNet
          </option>
        </Select>

        <Select
          placeholder="All Architectures"
          value={architecture}
          onChange={(e) => setArchitecture(e.target.value)}
        >
          {Object.values(EModelArchitecture).map((arch) => (
            <option key={arch} value={arch}>
              {arch}
            </option>
          ))}
        </Select>
      </HStack>

      <Input
        placeholder="Search models..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {results.length > 0 && (
        <VStack
          width="100%"
          spacing={2}
          maxH="400px"
          overflowY="auto"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          p={2}
        >
          {results.map((model) => (
            <Box
              key={model.air}
              width="100%"
              p={2}
              borderRadius="md"
              bg={bgColor}
              cursor="pointer"
              _hover={{ bg: hoverBg }}
              onClick={() => onModelSelect(model)}
            >
              <HStack spacing={4}>
                {model.heroImage && (
                  <Image
                    src={model.heroImage}
                    alt={model.name}
                    boxSize="50px"
                    objectFit="cover"
                    borderRadius="md"
                    filter={isNSFW(model.tags) ? "blur(3px)" : "none"}
                  />
                )}
                <VStack align="start" spacing={1} flex={1}>
                  <Text fontWeight="bold">{model.name}</Text>
                  <HStack spacing={2} wrap="wrap">
                    {model.tags.slice(0, 3).map((tag) => (
                      <Tag key={tag} size="sm">
                        {tag}
                      </Tag>
                    ))}
                  </HStack>
                  {/* {model.defaultScheduler && (
                    <Tag size="sm" colorScheme="orange">
                      {model.defaultScheduler.toUpperCase()}
                    </Tag>
                  )} */}
                </VStack>
                <Text fontSize="sm" color="gray.500">
                  {model.architecture.toUpperCase()}
                </Text>
                <Tooltip label="View Model on CivitAI">
                  <IconButton
                    aria-label="View Model"
                    icon={<ExternalLinkIcon />}
                    // bg="transparent"
                    onClick={() => {
                      const modelIdentifier = model.air.split(":")[1];
                      const modelId = modelIdentifier.split("@")[0];
                      const modelVersionId = modelIdentifier.split("@")[1];
                      window.open(
                        `https://civitai.com/models/${modelId}?modelVersionId=${modelVersionId}`,
                        "_blank"
                      );
                    }}
                  />
                </Tooltip>
              </HStack>
            </Box>
          ))}

          {hasMore && (
            <Box width="100%" p={2} textAlign="center">
              <Button
                size="sm"
                leftIcon={<ChevronDownIcon />}
                onClick={handleLoadMore}
              >
                {t("chat.modelInfo.loadMore")}
              </Button>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
};
